// Run a database write and SAY SO if it was refused. Ported from Tabletop Hub, where it exists because of a
// specific and expensive class of bug.
//
// ⚠ THE PROBLEM. A PostgREST call does not throw. `await supabase.from("x").update(…)` resolves happily whether
// the row changed or the database said no, and the error arrives as a FIELD on the result that nothing is
// obliged to read. Combine that with optimistic UI — set local state first, fire the query, move on — and
// success and failure render identically. That is the shape behind every dead path TTH shipped: a wall that
// stayed on screen and vanished on reload, a condition chip that flickered, a button that had been rejected
// every time since the day it was written.
//
// ⚠⚠ A try/catch AROUND THE CALL DOES NOT HELP, and that is the trap worth naming: the call does not throw, so
// the catch never runs and the code LOOKS protected.
//
// So: one helper, and the caller must supply the "say" function. There is deliberately no default — a silent
// fallback would recreate the whole problem behind a nicer-looking call.

/** What PostgREST hands back. Narrow on purpose: anything with an `error` fits, including a storage result. */
export type WriteResult = { error: { message: string } | null };

/** A result that also reports what it touched. Required by `writeOneOrSay` — see the note there. */
export type SelectResult<T> = { data: T[] | null; error: { message: string } | null };

/**
 * @param what what the user was doing, as a verb phrase — "save that map", "remove the door slam".
 *             ⚠ It is shown to a person, so `"Couldn't " + what` has to read as a sentence. Naming the ACTION
 *             rather than the table is the point: `asset_maps` means nothing to a GM mid-session.
 * @param run  the query, unevaluated. A thunk rather than a promise so nothing runs before we can guard it.
 * @param say  how to tell the user. No default, on purpose.
 * @returns true if the write landed.
 */
export async function writeOrSay(
  what: string,
  run: () => PromiseLike<WriteResult>,
  say: (message: string) => void,
): Promise<boolean> {
  let res: WriteResult;
  try {
    res = await run();
  } catch (e) {
    // Genuinely thrown errors are rare here (a network drop, an aborted fetch) but they exist, and letting one
    // escape would be the same silent failure by another route.
    say(`Couldn't ${what} — ${e instanceof Error ? e.message : "the request failed"}.`);
    return false;
  }
  if (res?.error) {
    say(`Couldn't ${what} — ${res.error.message}`);
    return false;
  }
  return true;
}

/**
 * Like `writeOrSay`, but ALSO refuses a write that matched no rows.
 *
 * ⚠ ZERO ROWS IS A REFUSAL TOO. PostgREST does not throw and does not complain when an UPDATE or DELETE matches
 * nothing — it returns success having done nothing at all. For a write that MUST land (renaming the map you are
 * looking at, revoking a share link) "it succeeded and changed nothing" is a failure wearing a success's
 * clothes, and it is indistinguishable from the real thing unless you look at the rows.
 *
 * ⚠ THE QUERY MUST END IN `.select()`. Without it PostgREST returns no rows even on success, and this helper
 * would report every write as refused. That is a loud, immediate failure rather than a silent one, which is the
 * right way round — but it is the mistake to expect.
 */
export async function writeOneOrSay<T>(
  what: string,
  run: () => PromiseLike<SelectResult<T>>,
  say: (message: string) => void,
): Promise<T | null> {
  let res: SelectResult<T>;
  try {
    res = await run();
  } catch (e) {
    say(`Couldn't ${what} — ${e instanceof Error ? e.message : "the request failed"}.`);
    return null;
  }
  if (res?.error) {
    say(`Couldn't ${what} — ${res.error.message}`);
    return null;
  }
  const rows = res?.data ?? [];
  if (rows.length === 0) {
    // Almost always one of: the row is gone, or a row-level security policy declined without saying so.
    say(`Couldn't ${what} — nothing changed. It may have been deleted, or it may not be yours to change. Reload the page to see what's really there.`);
    return null;
  }
  return rows[0] as T;
}

/**
 * Like `writeOneOrSay`, but for a write that should affect SEVERAL rows and returns all of them.
 *
 * ⚠ THE EXPECTED COUNT IS CHECKED, not just "more than zero". A batch insert of six goblins that lands four is
 * a partial success, and partial success is the failure mode a batch has that a single write does not: the
 * board ends up in a state neither the GM nor the undo stack agrees with. Better to say so than to look fine.
 *
 * ⚠ THE QUERY MUST END IN `.select()` — same as `writeOneOrSay`, same reason.
 */
export async function writeManyOrSay<T>(
  what: string,
  expected: number,
  run: () => PromiseLike<SelectResult<T>>,
  say: (message: string) => void,
): Promise<T[] | null> {
  let res: SelectResult<T>;
  try {
    res = await run();
  } catch (e) {
    say(`Couldn't ${what} — ${e instanceof Error ? e.message : "the request failed"}.`);
    return null;
  }
  if (res?.error) {
    say(`Couldn't ${what} — ${res.error.message}`);
    return null;
  }
  const rows = res?.data ?? [];
  if (rows.length === 0) {
    say(`Couldn't ${what} — nothing changed. It may have been deleted, or it may not be yours to change. Reload the page to see what's really there.`);
    return null;
  }
  if (rows.length !== expected) {
    say(`Only ${rows.length} of ${expected} were saved when trying to ${what}. Check the list and try the rest again.`);
  }
  return rows;
}
